import { Button, IconButton } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import { DragIndicator, RemoveCircleRounded } from '@material-ui/icons';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CancelIcon from '@material-ui/icons/Cancel';
import EditIcon from '@material-ui/icons/Edit';
import React, { useEffect, useReducer, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import CustomTextField from '../../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import { formDataValidate, handleValidation } from '../../../../../utils/formUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import { createHotelFloor, updateHotelFloor } from '../../../../../utils/ServicesAPIUtils';
import {
  allFloorsSelector,
  allRoomsSelector,
  fetchAllAccommodations,
} from '../../../../property/propertySlice';
import './Floor-Create.scss';
import CustomSearchSelect from '../../../../../shared/form-controls/custom-search-select/Custom-Seacrh-Select';
import { fetchAllBouquets } from '../../../../cms/cmsSlice';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { createIdFromLabel } from '../../../../../utils/commonUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';

const FloorCreate = (props) => {
  const dispatch = useDispatch();
  const { toggleDrawer, floor, hotelId, showLoader } = props;
  const [roomRangeValidation, setRoomRangeValidation] = useState(null);
  const [existRommError, setExistRoomError] = useState(false);
  const [invalidRangeRommError, setInvalidRangeRoomError] = useState(false);
  const [isTabInValid, setIsTabInValid] = useState(true);
  const [rangeToRoomNumbers, setRangeToRoomNumbers] = useState([]);
  const [rangeFromRoomNumbers, setRangeFromRoomNumbers] = useState([]);
  const roomListData = useSelector(allRoomsSelector);
  const [roomRangeFromSearchValue, setRangeFromRoomSearchValue] = useState('');
  const [roomRangeToSearchValue, setRangeToRoomSearchValue] = useState('');
  const RoomDetails = useSelector(allRoomsSelector).map((item) => parseInt(item.number));

  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    number: {
      value: '',
      fieldName: 'Floor number',
      errors: {},
      validations: ['required', 'positiveOnly'],
      tab: 0,
    },
    name: {
      value: '',
      fieldName: 'Floor name',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    rangeFrom: {
      value: '',
      fieldName: 'Rooms From',
      errors: {},
      validations: ['required', 'positiveOnly'],
      tab: 0,
    },
    rangeTo: {
      value: '',
      fieldName: 'Rooms up to',
      errors: {},
      validations: ['required', 'positiveOnly'],
      tab: 0,
    },
    rooms: {
      value: [],
      fieldName: 'Rooms',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
  });
  const [isSubmitted, setSubmitted] = useState(false);

  const onSearchRangeFromRoomList = async (searchRoom) => {
    setRangeFromRoomSearchValue(searchRoom);
    if (searchRoom === '') return setRangeFromRoomNumbers(roomListData);
    const filterList = await roomListData.filter((item) => {
      return item.number.toLowerCase().includes(searchRoom.toLowerCase());
    });
    return setRangeFromRoomNumbers(filterList);
  };

  const onSearchRangeToRoomList = async (searchRoom) => {
    setRangeToRoomSearchValue(searchRoom);
    if (searchRoom === '') return setRangeToRoomNumbers(roomListData);
    const filterList = await roomListData.filter((item) => {
      return item.number.toLowerCase().includes(searchRoom.toLowerCase());
    });
    return setRangeToRoomNumbers(filterList);
  };

  const grid = 8;
  const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: 'none',
    padding: 6,
    margin: `0 0 ${grid}px 0`,
    color: '#828282',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#FBFBFB',
    border: isDragging ? '2px solid #03E67C' : '2px solid #FBFBFB',
    ...draggableStyle,
  });

  const getListStyle = (isDraggingOver) => ({
    background: isDraggingOver ? '#CFF8E0' : '#FFF',
    padding: grid,
    width: '100%',
  });

  useEffect(() => {
    if (floor) {
      const currFields = { ...formData };
      currFields.number.value = floor.number;
      currFields.name.value = floor.name;
      currFields.rangeFrom.validations = [];
      currFields.rangeTo.validations = [];
      let index = 0;
      for (let range of floor.roomRangeList) {
        currFields.rooms.value.push({
          index: index,
          rangeFrom: range.rangeFrom,
          rangeTo: range.rangeTo,
        });
        index++;
      }

      setFormData(currFields);
    }
  }, [floor]);

  const validate = () => {
    const updatedFields = {};
    const isValid = formDataValidate(formData, updatedFields);
    setFormData(updatedFields);
    return isValid;
  };

  const onNumberChange = (e) => {
    if (e?.value === null) {
      const id = createIdFromLabel(formData.number.fieldName) + '_input';
      const domVal = document.getElementById(id)?.value;
      if (domVal === '0') {
        const currValidations = e?.validations
          ? e.validations.filter((v) => v !== 'positiveOnly')
          : ['required'];
        const currField = { ...e, value: 0, validations: currValidations };
        handleValidation(currField);
        setFormData({ number: currField });
        return;
      }
    }
    setFormData({ number: e });
  };

  const handleSubmit = async (e) => {
    setSubmitted(true);
    e.preventDefault();
    if (validate() && formData.rooms.value.length > 0) {
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      try {
        dispatch(stepUpLoaderCount());
        toggleDrawer('', false, '');
        if (floor) {
          await updateHotelFloor(
            {
              id: floor.id,
              number: Number(formData.number.value),
              name: formData.name.value,
              rooms: formData.rooms.value,
            },
            hotelId,
            language,
          );
          notificationManager('success', 'Floor updated successfully');
        } else {
          await createHotelFloor(
            {
              number: Number(formData.number.value),
              name: formData.name.value,
              rooms: formData.rooms.value,
            },
            hotelId,
            language,
          );
          notificationManager('success', 'Floor created successfully');
        }
        dispatch(fetchAllAccommodations(hotelId));
        dispatch(fetchAllBouquets(hotelId));
      } catch (error) {
        if (error?.graphQLErrors) {
          notificationManager('danger', error?.graphQLErrors?.[0]?.message);
        } else {
          floor
            ? notificationManager('danger', 'Floor updation failed')
            : notificationManager('danger', 'Floor creation failed');
        }
      } finally {
        dispatch(stepDownLoaderCount());
      }
    }
  };

  const onCancel = (e) => {
    toggleDrawer('', false, '');
  };

  useEffect(() => {
    setRoomRangeValidation(null);
    if (formData.rangeFrom.value && formData.rangeTo.value) {
      if (+formData.rangeFrom.value > +formData.rangeTo.value) {
        setRoomRangeValidation('Room range From value can not be higher than Upto Value');
      } else {
        setRoomRangeValidation(null);
      }
    }
  }, [formData.rangeFrom.value, formData.rangeTo.value]);

  const onRoomsAdd = () => {
    let range = [];
    const AvailableRooms = RoomDetails.map((item) => parseInt(item));

    const isExistRangeFromValue = formData.rooms.value.some(
      (elm) => elm.rangeFrom === formData.rangeFrom.value,
    );
    const isExistRangeToValue = formData.rooms.value.some(
      (elm) => elm.rangeTo === formData.rangeTo.value,
    );
    const checkRange = formData.rooms.value.every(
      (elm) =>
        (formData.rangeFrom.value > elm.rangeTo && formData.rangeTo.value > elm.rangeFrom) ||
        (formData.rangeTo.value < elm.rangeFrom && formData.rangeFrom.value < elm.rangeTo),
    );
    let checkRangeExistence = false;
    for (let i = parseInt(formData.rangeFrom.value); i <= parseInt(formData.rangeTo.value); i++) {
      if (!AvailableRooms.includes(i)) {
        checkRangeExistence = true;
        break;
      }
    }
    if (isExistRangeFromValue || isExistRangeToValue || !checkRange || checkRangeExistence) {
      if (checkRangeExistence) {
        setInvalidRangeRoomError(true);
        return;
      } else {
        setExistRoomError(true);
        return;
      }
    }
    setInvalidRangeRoomError(false);
    setExistRoomError(false);
    if (formData.rangeFrom.value && formData.rangeTo.value) {
      if (+formData.rangeFrom.value > +formData.rangeTo.value) {
        setRoomRangeValidation('Room range From value can not be higher than Upto Value');
      } else {
        setRoomRangeValidation('');
        const roomArray = [...formData.rooms.value];
        const room = {
          index: roomArray.length + 1,
          rangeFrom: formData.rangeFrom.value,
          rangeTo: formData.rangeTo.value,
        };

        roomArray.push(room);
        setFormData({
          rooms: {
            value: roomArray,
            fieldName: 'Rooms',
            errors: {},
            validations: [],
          },
          rangeFrom: {
            value: '',
            fieldName: 'Room From',
            errors: {},
            validations: [],
          },
          rangeTo: {
            value: '',
            fieldName: 'Room Upto',
            errors: {},
            validations: [],
          },
        });
      }
    }
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) return;
    const items = reorder(formData.rooms.value, result.source.index, result.destination.index);
    const rooms = { ...formData.rooms };
    rooms.value = items;
    setFormData({ rooms: rooms });
  };

  const onItemEdit = (index) => {
    const selectedItem = formData.rooms.value?.find((item, i) => i === index);
    const formDataCopy = { ...formData };
    formDataCopy.rangeFrom.value = selectedItem?.rangeFrom;
    formDataCopy.rangeTo.value = selectedItem?.rangeTo;
    formDataCopy.rooms.value.splice(index, 1);
    setFormData(formDataCopy);
  };
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onInfoDelete = (index) => {
    const roomArray = [...formData.rooms.value];
    roomArray.splice(index, 1);
    setFormData({
      rooms: {
        value: roomArray,
        fieldName: 'Rooms',
        errors: {},
        validations: [],
      },
      rangeFrom: {
        value: '',
        fieldName: 'Room From',
        errors: {},
        validations: roomArray.length === 0 ? ['required'] : [],
      },
      rangeTo: {
        value: '',
        fieldName: 'Room up to',
        errors: {},
        validations: roomArray.length === 0 ? ['required'] : [],
      },
    });
  };

  useEffect(() => {
    if (roomListData) setRangeFromRoomNumbers(roomListData);
  }, [setRangeFromRoomNumbers, roomListData]);

  useEffect(() => {
    if (roomListData) setRangeToRoomNumbers(roomListData);
  }, [setRangeToRoomNumbers, roomListData]);

  useEffect(() => {
    const isValid = formDataValidate(formData, false);
    setIsTabInValid(!isValid);
  }, [formData]);

  return (
    <form onSubmit={handleSubmit} className='floor-create' noValidate>
      <div className='container pt-4 d-flex align-items-end flex-column '>
        <div className='row px-3 w-100 mt-4'>
          <div className='col-md-6'>
            <span className='page-title'>{floor ? 'Edit Floor' : 'Add Floor'}</span>
          </div>
          <div className='col-md-6 d-flex justify-content-end'>
            <IconButton aria-label='delete' onClick={(e) => onCancel(e)}>
              <CancelIcon />
            </IconButton>
          </div>
        </div>
        <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
          <div className='row px-3 w-100 d-flex mt-2'>
            <div className='col-md-6 mb-3'>
              <CustomTextField
                item={formData.number}
                handleInputChange={(e) => onNumberChange(e)}
                type='number'
              />
            </div>
            <div className='col-md-6 mb-3'>
              <CustomTextField
                item={formData.name}
                handleInputChange={(e) => {
                  setFormData({ name: e });
                }}
                maxLength={100}
                endAdornment={true}
                characterCount={true}
                multiline={true}
              />
            </div>
          </div>
          <div className='row px-3 w-100'>
            <div className='col-md-6 mb-3'>
              <CustomSearchSelect
                item={formData.rangeFrom}
                handleInputChange={(e) => {
                  setExistRoomError(false);
                  setInvalidRangeRoomError(false);
                  setFormData({ rangeFrom: e });
                }}
                options={rangeFromRoomNumbers}
                hideNullOption={true}
                isSearchable={true}
                searchPlaceholder='Select from Room Number'
                valueField='number'
                labelField='number'
                searchValue={roomRangeFromSearchValue}
                getSearchInput={onSearchRangeFromRoomList}
              />
            </div>
            <div className='col-md-6 mb-3'>
              <CustomSearchSelect
                item={formData.rangeTo}
                handleInputChange={(e) => {
                  setExistRoomError(false);
                  setInvalidRangeRoomError(false);
                  setFormData({ rangeTo: e });
                }}
                options={rangeToRoomNumbers}
                hideNullOption={true}
                isSearchable={true}
                searchPlaceholder='Select to Room Number'
                valueField='number'
                labelField='number'
                searchValue={roomRangeToSearchValue}
                getSearchInput={onSearchRangeToRoomList}
              />
            </div>
          </div>
          <div className='row px-3 w-100'>
            <div className='col-md-12 mb-3'>
              <Button
                variant='outlined'
                color={formData.rangeTo.value && formData.rangeFrom.value ? 'primary' : 'secondary'}
                disabled={!formData.rangeTo.value || !formData.rangeFrom.value}
                onClick={onRoomsAdd}
              >
                Add
              </Button>
            </div>
            {roomRangeValidation && (
              <div className='col-md-12 mb-2'>
                <span className='input-error'>{roomRangeValidation}</span>
              </div>
            )}
            {isSubmitted && formData.rooms.value.length === 0 && (
              <div className='col-md-12 mb-2'>
                <span className='input-error'>Atleast 1 room range should be added</span>
              </div>
            )}
            {existRommError && (
              <div className='input-error mt-4'>
                <ErrorOutlineIcon className='me-1' fontSize='small' /> Unable to add Room(s) as it
                is already added to the selected floor
              </div>
            )}
            {invalidRangeRommError && (
              <div className='input-error mt-4'>
                <ErrorOutlineIcon className='me-1' fontSize='small' /> Rooms are missing in the
                above room range
              </div>
            )}
          </div>
          <div className='row px-3 w-100'>
            <div className='col-md-12 mb-3 drag-item'>
              <DragDropContext onDragEnd={(e) => onDragEnd(e)}>
                <Droppable droppableId='droppable'>
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={getListStyle(snapshot.isDraggingOver)}
                    >
                      {formData.rooms.value.map((item, index) => (
                        <Draggable
                          key={'' + item.index}
                          draggableId={'' + item.index}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={getItemStyle(
                                snapshot.isDragging,
                                provided.draggableProps.style,
                              )}
                              className='additional-info-list-item me-5'
                            >
                              <DragIndicator className='additional-info-drag col-2' />
                              <TextField
                                className='px-3'
                                id='additional-info-field-edit col-md-5'
                                value={item.rangeFrom}
                                InputProps={{
                                  readOnly: true,
                                }}
                              />
                              <TextField
                                className='px-3'
                                id='additional-info-value-edit col-md-5'
                                value={item.rangeTo}
                                InputProps={{
                                  readOnly: true,
                                }}
                              />
                              <IconButton onClick={(e) => onItemEdit(index, 'range')}>
                                <EditIcon fontSize='small' />
                              </IconButton>
                              <IconButton onClick={(e) => onInfoDelete(index)}>
                                <RemoveCircleRounded
                                  className='additional-info-remove'
                                  fontSize='small'
                                />
                              </IconButton>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
        </div>
        <div className='row mx-2 mt-auto px-4 w-100 pb-5'>
          <div className='col-md-12 d-flex justify-content-start'>
            <Button
              variant='contained'
              type='submit'
              color='primary'
              className='me-3'
              size='large'
              disabled={isTabInValid}
            >
              {floor ? 'Update' : 'Add'}
            </Button>
            <Button
              variant='outlined'
              type='button'
              color='secondary'
              size='large'
              onClick={(e) => onCancel(e)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default FloorCreate;
